import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { findPaidPayments } from "../../services/payment";
import formatMZN from "../../utils/formatMZN";
import { useTranslation } from "../../i18n/I18nContext";

const PAGE_SIZE = 6;

const isToday = (value) => {
  const date = new Date(value);
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

const formatDateTime = (value, locale) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const localeTag = locale === "en" ? "en-GB" : "pt-PT";
  const datePart = date.toLocaleDateString(localeTag, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(localeTag, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart} ${timePart}`;
};

const LatestAppointmentsOne = () => {
  const { t, locale } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    findPaidPayments()
      .then((resp) => {
        const todayPayments = (resp.data || [])
          .filter((payment) => isToday(payment.paymentDate))
          .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
        setPayments(todayPayments);
      })
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.max(1, Math.ceil(payments.length / PAGE_SIZE));

  const pagePayments = useMemo(
    () => payments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [payments, page],
  );

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
  };

  return (
    <div className='col-xxl-8'>
      <div className='card h-100'>
        <div className='card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between'>
          <h6 className='text-lg fw-semibold mb-0'>{t('dashboard.recentTransactions.title')}</h6>
          <span className='text-secondary-light text-sm'>{t('dashboard.today')}</span>
        </div>
        <div className='card-body p-0'>
          <div className='table-responsive scroll-sm'>
            <table className='table bordered-table mb-0 rounded-0 border-0'>
              <thead>
                <tr>
                  <th scope='col' className='bg-transparent rounded-0'>
                    {t('dashboard.recentTransactions.col.student')}
                  </th>
                  <th scope='col' className='bg-transparent rounded-0'>
                    {t('dashboard.recentTransactions.col.time')}
                  </th>
                  <th scope='col' className='bg-transparent rounded-0'>
                    {t('dashboard.recentTransactions.col.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={3} className='text-center text-secondary-light'>
                      {t('dashboard.loading')}
                    </td>
                  </tr>
                )}
                {!loading && payments.length === 0 && (
                  <tr>
                    <td colSpan={3} className='text-center text-secondary-light'>
                      {t('dashboard.recentTransactions.empty')}
                    </td>
                  </tr>
                )}
                {pagePayments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.student?.name || "-"}</td>
                    <td>{formatDateTime(payment.updatedAt, locale)}</td>
                    <td>
                      <span className='bg-success-focus text-success-main px-10 py-4 radius-8 fw-medium text-sm'>
                        {formatMZN(payment.total)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && payments.length > PAGE_SIZE && (
            <ul className='pagination d-flex flex-wrap align-items-center gap-2 justify-content-center py-16 mb-0'>
              <li className='page-item'>
                <button
                  type='button'
                  className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                >
                  <Icon icon='ep:d-arrow-left' className='text-xl' />
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <li key={p} className='page-item'>
                  <button
                    type='button'
                    className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                      p === page
                        ? "bg-primary-600 text-white"
                        : "bg-primary-50 text-secondary-light"
                    }`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                </li>
              ))}
              <li className='page-item'>
                <button
                  type='button'
                  className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                >
                  <Icon icon='ep:d-arrow-right' className='text-xl' />
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default LatestAppointmentsOne;
